// Section file — funnel team.
// Sections: login, register, registerProfile, registerSocial, registerCheckout,
// upsell, purchase, purchaseCheckout, purchaseUsername.
// Must keep an IDENTICAL key tree with es.funnel.js.
import React from "react";

const lang = {
  login: {
    passwordsDoNotMatch: "Passwords do not match",
    resetSuccess: "Password reset successful. Please login with your new password.",
    genericError: "An error occurred. Please try again.",
    newPasswordLabel: "New Password",
    newPasswordPlaceholder: "Enter new password",
    passwordHint: "Password must be at least 8 characters long",
    confirmPasswordLabel: "Confirm Password",
    confirmPasswordPlaceholder: "Confirm new password",
    resetPasswordButton: "Reset Password",
    resetSent: "Reset instructions sent to email",
    resetFailed: "Failed to send reset instructions",
    loading: "Loading...",
    resetModalTitle: "Reset Your Password",
    resetModalBody: "Enter your email address and we'll send you instructions to reset your password.",
    emailAddressLabel: "Email Address",
    emailPlaceholder: "name@example.com",
    sendResetInstructions: "Send Reset Instructions",
    heading: "Log In",
    subheading: "Sign in using your email and password to access the dashboard.",
    enterEmailPlaceholder: "Enter email",
    passwordPlaceholder: "Password",
    loginButton: "Login",
    forgotPassword: " Forgot your password? ",
    noAccount: (
      <>
        Don't have an account? <a className="text-primary" href="/pricing">Purchase a plan</a>
      </>
    ),
  },
  register: {
    stepSelectPlan: "Select Plan",
    stepSetupAccount: "Setup Account",
    stepBoostGrowth: "Boost Growth",
    stepGetStarted: "Get Started",
    continueToCheckout: "Continue to Checkout",
    profileLoadError: "Unable to load profile. Please try again later.",
    chartIconAlt: "Chart icon",
  },
  registerProfile: {
    heading: "Set up your account",
    subheading: "Register using your email to access the dashboard.",
    emailPlaceholder: "Email",
    emailAriaLabel: "Email",
    didYouMean: (suggestion) => (
      <>
        Did you mean <strong>{suggestion}</strong>?
      </>
    ),
    continue: "Continue",
    alreadyHaveAccount: (
      <>
        Already have an account?{" "}
        <a className="text-primary" href="/login">
          Login here
        </a>
      </>
    ),
  },
  registerSocial: {
    heading: "Link your profile",
    subheading: "We need your profile to start growing your followers. Your credentials are never stored.",
    searchFailed: "Failed to search for account.",
    linkedSuccess: (username, platform) => `Successfully linked ${username} ${platform} account.`,
    profileNotFound: "Profile not found. Please check your username and try again.",
    usernamePlaceholder: "Enter username",
    usernameAriaLabel: "Username",
    searchButton: "Search",
    linkButton: "Link",
    continue: "Continue",
    skipStep: (
      <>
        Want to skip this step?{" "}
        <a className="text-primary" href="/dashboard">
          Continue without linking
        </a>
      </>
    ),
  },
  registerCheckout: {
    unknownError: "Unknown error",
    invalidMonth: "Invalid month",
    cardExpired: "Card has expired",
    errorUpdatingEmail: "Error updating email",
    enterPassword: "Please enter a password",
    setPasswordFailed: "Failed to set password. Please try again.",
    allSet: "You're all set!",
    planActive: (plan) => `Your ${plan} plan is active. Set a password to access your dashboard.`,
    createPasswordPlaceholder: "Create a password",
    setPasswordButton: "Set Password & Go to Dashboard",
    perMonthStrike: (n) => `$${n}/mo`,
    perMonthSuffix: "/mo",
    perYear: (n) => `$${n}/yr`,
    savePerYear: (n) => ` · Save $${n}/year`,
    followersLine: (range) => `${range} monthly followers. Growth starts within 24 hours.`,
    chargedToday: (amount) => (
      <>
        You'll be charged <strong style={{ color: '#0a2540' }}>${amount}</strong> today for 1 year of access.
      </>
    ),
    // Recurring toggle. On by default; off buys a single billing period.
    recurringLabel: "Auto-renew my plan",
    recurringOnHelp: (interval) => `Renews automatically every ${interval}. Cancel anytime.`,
    recurringOffHelp: (interval) => `One-time purchase — you'll get ${interval} of access and won't be charged again.`,
    recurringIntervalMonth: "month",
    recurringIntervalYear: "year",
    recurringIntervalOneMonth: "1 month",
    recurringIntervalOneYear: "1 year",
    annualPlanSelected: "Annual plan selected",
    savingPerYear: (savings) => (
      <>
        You're saving <strong>${savings}</strong> per year
      </>
    ),
    accountLabel: "Account",
    change: "Change",
    edit: "Edit",
    orderSummary: "Order summary",
    planLine: (plan, interval) => `${plan} plan (${interval})`,
    intervalAnnual: "annual",
    intervalMonthly: "monthly",
    likesAddon: "More Likes add-on",
    viewsAddon: "More Views add-on",
    engagementAddon: "Engagement add-on",
    annualDiscount: "Annual discount",
    totalBilledToday: "Total billed today",
    dueToday: "Due today",
    thatsPerMonth: (n) => `That's $${n}/mo`,
    payment: "Payment",
    newCard: "New card",
    cardNumberLabel: "Card number",
    expirationLabel: "Expiration",
    cvcLabel: "CVC",
    expPlaceholder: "MM / YY",
    cvcPlaceholder: "CVC",
    paymentFailed: "Payment failed",
    processing: "Processing...",
    startMyGrowth: "Start My Growth",
    loadingPaymentOptions: "Loading payment options...",
    secureCheckout: "Secure checkout",
    cancelAnytime: "Cancel anytime",
    guarantee: "7-day guarantee",
    changeEmailTitle: "Change email",
    newEmailLabel: "New email address",
    emailPlaceholder: "you@example.com",
    cancel: "Cancel",
    save: "Save",
  },
  upsell: {
    options: {
      likes: {
        title: "More Likes",
        description: "We will share your newest posts up to 10x more to your specific audience after every post to maximize likes!",
      },
      views: {
        title: "More Views",
        description: "We will share your newest posts up to 10x more to your specific audience daily to maximize views and growth!",
      },
      engagement: {
        title: "Engagement",
        description: "We will share your posts and stories up to 12x more to maximize your engagement!",
      },
    },
    errorUpdating: (msg) => `Error updating upsells: ${msg}`,
    errorUpdatingFallback: "Error updating upsells",
    manageAddons: "Manage Your Add-ons",
    boostYourGrowth: "Boost Your Growth",
    manageSubheading: "Add or remove extras to customize your growth experience.",
    boostSubheading: "Add powerful extras to supercharge your results.",
    activeList: (list) => `Active: ${list}`,
    activeBadge: "Active",
    perMo: "/mo",
    billChange: (isAnnualPlan, isIncrease, amount) => `Your ${isAnnualPlan ? 'annual' : 'monthly'} bill will ${isIncrease ? 'increase' : 'decrease'} by $${amount}/${isAnnualPlan ? 'yr' : 'mo'}`,
    saving: "Saving...",
    saveChanges: "Save Changes",
    confirmTitle: "Confirm Add-on Changes",
    changesSummary: "Changes Summary",
    adding: "Adding",
    removing: "Removing",
    yr: "yr",
    mo: "mo",
    periodChange: (isAnnualPlan) => `${isAnnualPlan ? 'Annual' : 'Monthly'} change`,
    cancel: "Cancel",
    processing: "Processing...",
    confirmChanges: "Confirm Changes",
  },
  purchase: {
    unknownError: "Unknown error",
    purchaseComplete: "Purchase complete!",
    errorLoggingIn: (msg) => `Error logging in: ${msg}`,
  },
  purchaseCheckout: {
    plans: {
      pro: {
        name: "Pro Plan",
        features: [
          { feature: "Instant Delivery", description: "Get your followers instantly after payment." },
          { feature: "7,500 - 10,000 Followers", description: "Get 7,500 - 10,000 followers per month." },
          { feature: "500 Boost Balance", description: "Use boost balance to send Likes, Views, and Comments to your posts." },
          { feature: "Daily Bonuses", description: "Earn daily bonuses to gain more balance" },
          { feature: "AI Caption Generator", description: "Generate captions for your posts using AI." },
          { feature: "Idea Generator", description: "Get ideas for your next post." },
          { feature: "Profile Enhancer", description: "Analyze your profile to get more followers." },
          { feature: "Anonymous Story Viewer", description: "View stories anonymously." },
          { feature: "HQ Post Downloader", description: "Download high-quality posts." },
        ],
      },
      basic: {
        name: "Basic Plan",
        features: [
          { feature: "Instant Delivery", description: "Get your followers instantly after payment." },
          { feature: "2,000 - 3,000 Followers", description: "Get 2,000 - 3,000 followers per month." },
          { feature: "500 Boost Balance", description: "Use boost balance to send Likes, Views, and Comments to your posts." },
          { feature: "Daily Bonuses", description: "Earn daily bonuses to gain more balance" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
        ],
      },
      starter: {
        name: "Starter Plan",
        features: [
          { feature: "500 - 1,000 Followers", description: "Gain 500 - 1,000 real followers per month without lifting a finger." },
          { feature: "$5 Boost Credits ⓘ", description: "Redeem your boost credit for Followers, Likes, Views, and Comments." },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
          { feature: "" },
        ],
      },
    },
    heading: "Payment Details",
    subheading: "This is to verify your account, you will not be charged during your 3 day free trial.",
    annualButton: "annual",
    save50: "Save 50%",
    monthlyButton: "Monthly",
    proPlanLabel: "Pro Plan",
    fiftyOffBadge: "50% Off",
    monthlyPlanBadge: "Monthly Plan",
    billedAnnually: (price) => `Billed annually at $${price}/mo`,
    billedMonthly: (price) => `Billed monthly at $${price}/mo`,
    balanceOption: (balance) => `${balance} Balance`,
    balancePrice: (price) => `+$${price}/mo`,
    cardNumberPlaceholder: "Card Number",
    cardNumberAria: "Card Number",
    expPlaceholder: "MM / YY",
    expAria: "Card expiration date",
    cvvPlaceholder: "CVV",
    cvvAria: "CVV",
    loading: "Loading...",
    startFreeTrial: "Start Free Trial",
    whatsIncluded: "What's included in the free trial",
  },
  purchaseUsername: {
    heading: "Set up your account",
    subheading: "Register using your email to access the dashboard.",
    fullNamePlaceholder: "Full Name",
    fullNameAria: "InstFull Name",
    emailPlaceholder: "Email",
    emailAria: "Email",
    passwordPlaceholder: "Password",
    passwordAria: "Password",
    continue: "Continue",
    alreadyHaveAccount: (
      <>
        Already have an account?{" "}
        <a className="text-primary" href="/login">
          Login here
        </a>
      </>
    ),
  },
};

export default lang;
